/**
 * Currency helpers (iter71 — Dynamic Currency).
 *
 * The organisation picks a `default_currency` in Settings. From that point on,
 * every *newly created* shift / expense is stamped with that currency code
 * server-side. Historical records keep whichever currency they were stamped
 * with (falling back to GBP for anything saved before this feature shipped).
 *
 * UI contract:
 *   - For a specific record (expense, shift, rate): pass `record.currency`
 *     to `symbolFor` / `fmt`. Fallback is GBP.
 *   - For brand-new amounts (totals/stats showing current-period data,
 *     "Default Hourly Rate" input, etc.): pass the user's organisation
 *     currency from `user.organisation_currency`.
 */

// Keep this in sync with /app/backend/config/currencies.py. We copy here so
// the frontend can format offline without an extra fetch on every page.
export const CURRENCIES = [
  { code: 'GBP', symbol: '£', name: 'Pound Sterling', decimals: 2 },
  { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2 },
  { code: 'EUR', symbol: '€', name: 'Euro', decimals: 2 },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', decimals: 2 },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', decimals: 2 },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', decimals: 2 },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', decimals: 2 },
  { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', decimals: 0 },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', decimals: 2 },
  { code: 'RWF', symbol: 'FRw', name: 'Rwandan Franc', decimals: 0 },
  { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound', decimals: 2 },
  { code: 'MAD', symbol: 'DH', name: 'Moroccan Dirham', decimals: 2 },
  { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc', decimals: 0 },
  { code: 'XAF', symbol: 'FCFA', name: 'Central African CFA Franc', decimals: 0 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', decimals: 2 },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', decimals: 2 },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', decimals: 2 },
  { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', decimals: 2 },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka', decimals: 2 },
  { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee', decimals: 2 },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', decimals: 2 },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', decimals: 2 },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', decimals: 0 },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', decimals: 2 },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won', decimals: 0 },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', decimals: 2 },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', decimals: 2 },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', decimals: 2 },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', decimals: 0 },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', decimals: 0 },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham', decimals: 2 },
  { code: 'SAR', symbol: 'SR', name: 'Saudi Riyal', decimals: 2 },
  { code: 'QAR', symbol: 'QR', name: 'Qatari Riyal', decimals: 2 },
  { code: 'ILS', symbol: '₪', name: 'Israeli New Shekel', decimals: 2 },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira', decimals: 2 },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', decimals: 2 },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', decimals: 2 },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', decimals: 2 },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', decimals: 2 },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty', decimals: 2 },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', decimals: 2 },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint', decimals: 2 },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu', decimals: 2 },
  { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev', decimals: 2 },
  { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia', decimals: 2 },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble', decimals: 2 },
  { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso', decimals: 2 },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', decimals: 2 },
  { code: 'ARS', symbol: 'AR$', name: 'Argentine Peso', decimals: 2 },
  { code: 'CLP', symbol: 'CLP$', name: 'Chilean Peso', decimals: 0 },
  { code: 'COP', symbol: 'COL$', name: 'Colombian Peso', decimals: 2 },
  { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol', decimals: 2 },
];

export const DEFAULT_CURRENCY = 'GBP';

const BY_CODE = CURRENCIES.reduce((acc, c) => {
  acc[c.code] = c;
  return acc;
}, {});

export function getCurrency(code) {
  if (typeof code !== 'string') return BY_CODE[DEFAULT_CURRENCY];
  return BY_CODE[code.toUpperCase()] || BY_CODE[DEFAULT_CURRENCY];
}

/** Just the symbol for the given currency code. Fallback: £. */
export function symbolFor(code) {
  return getCurrency(code).symbol;
}

/**
 * Format a number as `${symbol}${amount}`.
 *
 * @param {number} amount
 * @param {string} [code] currency code; falls back to GBP for null/invalid.
 * @param {object} [opts]
 * @param {number} [opts.decimals] override the currency's natural decimals.
 */
export function fmt(amount, code, opts = {}) {
  const cur = getCurrency(code);
  const decimals = opts.decimals ?? cur.decimals;
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  return `${cur.symbol}${safe.toFixed(decimals)}`;
}
